export const IMPERSONATION_STORAGE_KEY = 'admin_impersonate_user_id';
export const IMPERSONATION_STATE_KEY = 'admin_impersonation_state';

export interface ImpersonationState {
  userId: string | null;
  userEmail: string | null;
  originalToken?: string | null;
  isImpersonating?: boolean;
}

export const readImpersonationId = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.localStorage.getItem(IMPERSONATION_STORAGE_KEY);
  } catch (err) {
    console.error('Failed to read impersonation state from localStorage:', err);
    return null;
  }
};

export const clearImpersonationId = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch (err) {
    console.error('Failed to clear impersonation state from localStorage:', err);
  }
};

export const setImpersonationId = (userId: string | null) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (userId) {
      window.localStorage.setItem(IMPERSONATION_STORAGE_KEY, userId);
    } else {
      window.localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  } catch (err) {
    console.error('Failed to update impersonation state in localStorage:', err);
  }
};

export const withImpersonationHeader = <T extends Record<string, string>>(headers: T): T => {
  const impersonatedUserId = readImpersonationId();
  if (impersonatedUserId) {
    return {
      ...headers,
      'X-Impersonate-User': impersonatedUserId,
    } as T;
  }
  return headers;
};

export const readImpersonationState = (): ImpersonationState | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(IMPERSONATION_STATE_KEY);
    if (!stored) {
      const userId = readImpersonationId();
      return userId ? { userId, userEmail: null } : null;
    }
    const parsed = JSON.parse(stored);
    const userId = parsed?.targetUserId ?? parsed?.userId ?? null;
    const userEmail = parsed?.targetUserEmail ?? parsed?.userEmail ?? null;
    return {
      userId,
      userEmail,
      originalToken: parsed?.originalToken ?? null,
      isImpersonating: Boolean(parsed?.isImpersonating ?? userId),
    };
  } catch (err) {
    console.error('Failed to parse impersonation state from localStorage:', err);
    return null;
  }
};
